import React, { useState, useEffect } from 'react';
import { checkSubscription, getUserPerformance, getUserSessions, updateStreak, saveReview } from './supabaseClient';
import PrintReport from './PrintReport';
import { LogoIcon } from './Logo';
import { useLanguage } from './LanguageContext';

const PLAN_META = {
  basic: { nameKey: 'plans_basic', icon: '⭐', color: '#3b82f6', subjectKeys: ['mode_quant', 'mode_verbal'] },
  pro:   { nameKey: 'plans_pro',   icon: '🚀', color: '#7c6df8', subjectKeys: ['mode_quant', 'mode_verbal', 'mode_tahsili', 'STEP'] },
  elite: { nameKey: 'plans_elite', icon: '🌍', color: '#f59e0b', subjectKeys: ['mode_quant', 'mode_verbal', 'mode_tahsili', 'STEP', 'mode_tajamuat'] },
};

const MODES = [
  { id: 'كمي',      icon: '🔢', color: '#3b82f6', labelKey: 'mode_quant',     plans: ['basic','pro','elite'] },
  { id: 'لفظي',     icon: '📖', color: '#7c6df8', labelKey: 'mode_verbal',    plans: ['basic','pro','elite'] },
  { id: 'تحصيلي',   icon: '🎓', color: '#10b981', labelKey: 'mode_tahsili',   plans: ['pro','elite'] },
  { id: 'step',     icon: '🌍', color: '#f59e0b', labelKey: null, label: 'STEP', plans: ['pro','elite'], comingSoon: true },
  { id: 'tajamuat', icon: '🗂️', color: '#e879f9', labelKey: 'mode_tajamuat',  plans: ['guest','basic','pro','elite'] },
];

function daysLeft(expiresAt) {
  const diff = new Date(expiresAt) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function StatCard({ icon, value, label, color }) {
  return (
    <div style={{
      background: 'var(--bg-card)', borderRadius: 16, padding: '16px 12px',
      border: '1px solid #334155', textAlign: 'center', flex: 1,
    }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
      <div style={{ color: color || '#7c6df8', fontSize: 22, fontWeight: 900 }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ── حساب الـ Streak للضيف (localStorage fallback) ─────────
function calcStreakLocal() {
  const today     = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const raw       = JSON.parse(localStorage.getItem('qadder_streak') || '{}');
  let current;
  if (raw.lastDate === today)      current = raw.current || 1;
  else if (raw.lastDate === yesterday) current = (raw.current || 0) + 1;
  else                              current = 1;
  const longest = Math.max(current, raw.longest || 0);
  localStorage.setItem('qadder_streak', JSON.stringify({ current, longest, lastDate: today }));
  return { current, longest };
}

function streakBadge(n) {
  if (n >= 30) return { icon: '🏆', labelKey: 'badge_champion' };
  if (n >= 14) return { icon: '💫', labelKey: 'badge_advanced' };
  if (n >= 7)  return { icon: '🔥', labelKey: 'badge_committed' };
  if (n >= 3)  return { icon: '⭐', labelKey: 'badge_regular' };
  return       { icon: '🌱', labelKey: 'badge_beginner' };
}

export default function HomeScreen({ user, plan, onLogout, onStartStudy, onStartTrial, onStartExam, onAdmin, onWrongAnswers, onPlans, theme, setTheme }) {
  const { t, isRTL } = useLanguage();
  const [sub, setSub]         = useState(null);
  const [perf, setPerf]       = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak]   = useState({ current: 0, longest: 0 });
  const [showReport, setShowReport] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewName, setReviewName] = useState('');
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSent, setReviewSent] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  const PLAN_MAP = { 'شامل': 'elite', 'متقدم': 'pro', 'أساسي': 'basic', 'elite': 'elite', 'pro': 'pro', 'basic': 'basic' };
  const planId   = PLAN_MAP[plan?.name_ar] || PLAN_MAP[plan?.name] || PLAN_MAP[plan?.id] || 'none';
  const planInfoRaw = PLAN_META[planId] || { nameKey: 'plan_noSub', icon: '🔒', color: '#64748b', subjectKeys: [] };
  const planInfo = {
    ...planInfoRaw,
    name: t(planInfoRaw.nameKey),
    subjects: planInfoRaw.subjectKeys.map(k => k === 'STEP' ? 'STEP' : t(k)),
  };

  useEffect(() => {
    if (!user?.id) {
      setStreak(calcStreakLocal()); // ضيف → localStorage
      setLoading(false);            // مهم: بدونها تعلق شاشة "جاري تحميل بياناتك..."
      return;
    }
    const load = async () => {
      try {
        // تحديث الـ Streak في Supabase للمستخدم المسجّل
        const streakData = await updateStreak(user.id);
        setStreak(streakData);

        const [subRes, perfRes, sessRes] = await Promise.all([
          checkSubscription(user.id),
          getUserPerformance(user.id),
          getUserSessions(user.id, 5),
        ]);
        setSub(subRes.data);
        setPerf(perfRes.data);
        setSessions(sessRes.data || []);
      } catch (e) {
        console.error('[HomeScreen load]', e);
      } finally {
        setLoading(false);          // تنتهي دائماً حتى لو فشل أي طلب
      }
    };
    load();
  }, [user]);

  const days      = sub?.expires_at ? daysLeft(sub.expires_at) : 0;
  const accuracy  = perf?.avg_score       ? Math.round(perf.avg_score)       : 0;
  const totalQ    = perf?.total_questions ? perf.total_questions             : 0;
  const totalSess = perf?.total_sessions  ? perf.total_sessions              : 0;

  const wrap = (children) => (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      fontFamily: 'Tajawal,sans-serif', direction: isRTL ? 'rtl' : 'ltr',
      padding: '0 0 40px',
      color: 'var(--text)',
    }}>
      {children}
    </div>
  );

  if (loading) return wrap(
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', color: '#7c6df8', fontSize: 18,
    }}>
      {t('loadingData')}
    </div>
  );

  return wrap(
    <>
      {showReport && (
        <PrintReport
          user={user} plan={plan} perf={perf} sub={sub}
          sessions={sessions}
          onClose={() => setShowReport(false)}
        />
      )}
      {/* ── Header ── */}
      <div style={{
        background: 'var(--bg-header)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border-header)',
        padding: '16px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoIcon size={36} />
          <div>
            <div
              onClick={onAdmin}
              style={{
                color: 'var(--gold)', fontWeight: 900, fontSize: 18, lineHeight: 1,
                cursor: onAdmin ? 'pointer' : 'default',
              }}
            >{t('appName')}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{t('appSlogan')}</div>
          </div>
        </div>

        {/* أزرار اليمين */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

          {!user?.isGuest && user?.phone && (
            <div style={{ color: 'var(--text-muted)', fontSize: 12, direction: 'ltr' }}>
              {user.phone.replace(/^966/, '0')}
            </div>
          )}

          <button onClick={onLogout} style={{
            background: user?.isGuest ? 'linear-gradient(135deg,#7c6df8,#a78bfa)' : 'transparent',
            border: user?.isGuest ? 'none' : '1px solid var(--border-header)', borderRadius: 10,
            color: user?.isGuest ? '#fff' : 'var(--text-secondary)', fontSize: 13, padding: '7px 14px',
            fontFamily: 'Tajawal,sans-serif', cursor: 'pointer', fontWeight: user?.isGuest ? 700 : 400,
          }}>
            {user?.isGuest ? t('home_registerNow') : t('logout')}
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 16px', maxWidth: 520, margin: '0 auto' }}>

        {/* ── بانر الضيف ── */}
        {user?.isGuest && (
          <div style={{
            background: 'linear-gradient(135deg,rgba(124,109,248,0.15),rgba(124,109,248,0.08))',
            border: '1.5px solid rgba(124,109,248,0.4)',
            borderRadius: 16, padding: '14px 18px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div>
              <div style={{ color: '#a78bfa', fontWeight: 800, fontSize: 15 }}>
                {t('home_guestBanner')}
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3 }}>
                {t('home_guestDesc')}
              </div>
            </div>
            <button onClick={onLogout} style={{
              background: 'linear-gradient(135deg,#7c6df8,#a78bfa)',
              border: 'none', borderRadius: 10, color: '#fff',
              fontSize: 13, fontWeight: 700, padding: '8px 16px',
              fontFamily: 'Tajawal,sans-serif', cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              {t('home_registerNow')}
            </button>
          </div>
        )}

        {/* ── بطاقة الاشتراك ── */}
        <div style={{
          background: `linear-gradient(135deg, ${planInfo.color}22, ${planInfo.color}11)`,
          border: `1px solid ${planInfo.color}44`,
          borderRadius: 20, padding: '20px',
          marginBottom: 20, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: -20, left: -20,
            fontSize: 80, opacity: 0.06, transform: 'rotate(-15deg)',
          }}>{planInfo.icon}</div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 4 }}>{t('home_currentPlan')}</div>
              <div style={{ color: '#fff', fontSize: 22, fontWeight: 900 }}>
                {planInfo.icon} {t('home_planBadge')} {planInfo.name}
              </div>
              <div style={{ color: planInfo.color, fontSize: 13, marginTop: 4, fontWeight: 600 }}>
                {planInfo.subjects.join(' • ')}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                background: days > 7 ? '#14532d' : days > 3 ? '#713f12' : '#450a0a',
                border: `1px solid ${days > 7 ? '#22c55e' : days > 3 ? '#f59e0b' : '#ef4444'}44`,
                borderRadius: 12, padding: '8px 14px',
              }}>
                <div style={{
                  color: days > 7 ? '#86efac' : days > 3 ? '#fbbf24' : '#fca5a5',
                  fontSize: 24, fontWeight: 900, lineHeight: 1,
                }}>{days}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('daysLeft')}</div>
              </div>
            </div>
          </div>

          {sub && days <= 7 && (
            <button onClick={onPlans} style={{
              marginTop: 12, width: '100%',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: 10, padding: '10px 12px',
              color: '#fca5a5', fontSize: 13, fontWeight: 700,
              fontFamily: 'Tajawal,sans-serif', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>{t('home_renewWarning')}</span>
              <span style={{ fontSize: 16 }}>←</span>
            </button>
          )}
        </div>

        {/* ── إحصائياتك ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 700 }}>{t('home_stats')}</div>
            {!user?.isGuest && planId !== 'none' && (
              <button onClick={() => setShowReport(true)} style={{
                background: 'linear-gradient(135deg,#1a1060,#2d1b8e)',
                border: '1px solid rgba(255,215,0,0.3)',
                borderRadius: 8, padding: '5px 12px',
                color: '#FFD700', fontSize: 12, fontWeight: 700,
                fontFamily: 'Tajawal,sans-serif', cursor: 'pointer',
              }}>
                {t('home_pdfReport')}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <StatCard icon="✅" value={totalQ.toLocaleString()} label={t('home_questionsAnswered')} color="#22c55e" />
            <StatCard icon="🎯" value={`${accuracy}%`}          label={t('home_accuracy')}          color="#7c6df8" />
            <StatCard icon="🔁" value={totalSess}               label={t('home_trainingSessions')}  color="#f59e0b" />
          </div>

          {/* ── تفصيل بالمادة ── */}
          {perf?.by_mode && Object.keys(perf.by_mode).length > 0 && (
            <div style={{ marginTop: 12 }}>
              {[
                { key: 'كمي',     icon: '🔢', color: '#3b82f6', label: t('mode_quant')    },
                { key: 'لفظي',    icon: '📖', color: '#7c6df8', label: t('mode_verbal')   },
                { key: 'تحصيلي',  icon: '🎓', color: '#10b981', label: t('mode_tahsili')  },
                { key: 'step',    icon: '🌍', color: '#f59e0b', label: 'STEP'               },
              ].filter(m => perf.by_mode[m.key]).map(m => {
                const d = perf.by_mode[m.key];
                return (
                  <div key={m.key} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '9px 14px', marginBottom: 6,
                    background: 'var(--bg-card)', borderRadius: 12,
                    border: `1px solid ${m.color}33`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>{m.icon}</span>
                      <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 700 }}>{m.label}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                        {d.sessions} {t('home_sessions')} • {d.questions} {t('home_questions')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* شريط التقدم */}
                      <div style={{ width: 60, height: 5, background: 'var(--bg-card-alt)', borderRadius: 10, overflow: 'hidden' }}>
                        <div style={{ width: `${d.accuracy}%`, height: '100%', background: m.color, borderRadius: 10 }} />
                      </div>
                      <span style={{ color: m.color, fontSize: 13, fontWeight: 900, minWidth: 36, textAlign: 'left' }}>
                        {d.accuracy}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Streak يومي ── */}
        {(() => {
          const { icon, labelKey } = streakBadge(streak.current);
          const label = t(labelKey);
          const fire = streak.current >= 3;
          return (
            <div style={{
              background: fire
                ? 'linear-gradient(135deg,rgba(245,158,11,0.13),rgba(239,68,68,0.08))'
                : 'rgba(255,255,255,0.03)',
              border: `1.5px solid ${fire ? 'rgba(245,158,11,0.4)' : 'var(--border)'}`,
              borderRadius: 16, padding: '14px 18px',
              marginBottom: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 38, lineHeight: 1 }}>
                  {fire ? '🔥' : '🌱'}
                </div>
                <div>
                  <div style={{
                    color: fire ? '#fbbf24' : 'var(--text-secondary)',
                    fontWeight: 900, fontSize: 22, lineHeight: 1,
                  }}>
                    {streak.current} {streak.current === 1 ? t('home_day') : t('home_days')}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3 }}>
                    {t('home_streak')}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: fire ? 'rgba(245,158,11,0.15)' : 'var(--bg-card-alt)',
                  border: `1px solid ${fire ? 'rgba(245,158,11,0.3)' : 'var(--border)'}`,
                  borderRadius: 10, padding: '6px 12px',
                }}>
                  <div style={{ fontSize: 18 }}>{icon}</div>
                  <div style={{ color: fire ? '#f59e0b' : 'var(--text-muted)', fontSize: 11, fontWeight: 700 }}>
                    {label}
                  </div>
                </div>
                {streak.longest > 1 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 10, marginTop: 4 }}>
                    {t('home_streakBest')} {streak.longest}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── ابدأ تدريباً ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            {t('home_startTraining')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {MODES.map(mode => {
              // tajamuat يفتح للجميع — الاشتراك يُطلب داخل شاشة الدوائر
              const isTajamuat  = mode.id === 'tajamuat';
              const locked      = !isTajamuat && (!mode.plans.includes(planId) || !!user?.isGuest);
              const comingSoon  = !!mode.comingSoon;
              const disabled    = locked || comingSoon;
              return (
                <button
                  key={mode.id}
                  onClick={() => {
                    if (isTajamuat) {
                      if (user?.isGuest) { onLogout(); return; }
                      if (onStartStudy) onStartStudy(mode.id);
                      return;
                    }
                    if (locked && !user?.isGuest && onPlans) { onPlans(); return; }
                    if (user?.isGuest) { onLogout(); return; }
                    if (!disabled && onStartStudy) onStartStudy(mode.id);
                  }}
                  style={{
                    background: disabled ? 'var(--bg-card-alt)' : `${mode.color}18`,
                    border: `1.5px solid ${disabled ? 'var(--border)' : mode.color + '55'}`,
                    borderRadius: 16, padding: '18px 14px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    textAlign: 'right', fontFamily: 'Tajawal,sans-serif',
                    position: 'relative', overflow: 'hidden',
                    transition: 'all 0.2s',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 6 }}>{mode.icon}</div>
                  <div style={{ color: disabled ? 'var(--text-muted)' : 'var(--text)', fontWeight: 700, fontSize: 15 }}>
                    {mode.labelKey ? t(mode.labelKey) : mode.label}
                  </div>
                  {comingSoon && (
                    <div style={{
                      position: 'absolute', top: 8, left: 8,
                      background: '#ef4444', color: '#fff',
                      fontSize: 9, fontWeight: 900,
                      padding: '2px 7px', borderRadius: 6,
                    }}>{t('home_comingSoon')}</div>
                  )}
                  {locked && !comingSoon && (
                    <div style={{
                      position: 'absolute', top: 8, left: 8,
                      background: '#f59e0b', color: '#000',
                      fontSize: 9, fontWeight: 900,
                      padding: '2px 7px', borderRadius: 6,
                    }}>{t('home_upgrade')}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── ترقية الباقة ── */}
        {planId !== 'elite' && !user?.isGuest && onPlans && (
          <button onClick={onPlans} style={{
            width: '100%', marginBottom: 14,
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #f59e0b18, #7c6df818)',
            border: '1.5px dashed #f59e0b88',
            borderRadius: 14, cursor: 'pointer',
            fontFamily: 'Tajawal,sans-serif', fontSize: 13,
            color: '#f59e0b', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            {t('home_upgradePlan')}
          </button>
        )}

        {/* ── أسئلتي الخاطئة ── */}
        <button onClick={user?.isGuest ? onLogout : onWrongAnswers} style={{
          width: '100%', marginBottom: 12,
          background: 'linear-gradient(135deg,rgba(239,68,68,0.1),rgba(239,68,68,0.06))',
          border: '1.5px solid rgba(239,68,68,0.3)',
          borderRadius: 16, padding: '16px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
        }}>
          <div style={{
            background: 'rgba(239,68,68,0.15)', borderRadius: 10,
            padding: '6px 10px', fontSize: 22,
          }}>📋</div>
          <div style={{ textAlign: 'right', flex: 1, marginRight: 12 }}>
            <div style={{ color: '#fca5a5', fontWeight: 900, fontSize: 16 }}>{t('home_wrongAnswers')}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 3 }}>
              {t('home_wrongAnswersDesc')}
            </div>
          </div>
          <span style={{ color: '#fca5a5', fontSize: 18 }}>←</span>
        </button>

        {/* ── محاكاة الاختبار الرسمي ── */}
        <button onClick={user?.isGuest ? onLogout : onStartExam} style={{
          width: '100%', marginBottom: 12,
          background: 'linear-gradient(135deg,rgba(34,197,94,0.12),rgba(16,185,129,0.08))',
          border: '1.5px solid rgba(34,197,94,0.4)',
          borderRadius: 16, padding: '16px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
          boxShadow: '0 4px 20px rgba(34,197,94,0.1)',
        }}>
          <div style={{
            background: 'rgba(34,197,94,0.15)', borderRadius: 10,
            padding: '6px 10px', fontSize: 22,
          }}>🏆</div>
          <div style={{ textAlign: 'right', flex: 1, marginRight: 12 }}>
            <div style={{ color: '#22c55e', fontWeight: 900, fontSize: 16 }}>{t('home_officialExam')}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 3 }}>
              {t('home_officialExamDesc')}
            </div>
          </div>
          <span style={{ color: '#22c55e', fontSize: 18 }}>←</span>
        </button>

        {/* ── اختبار تجريبي ── */}
        {onStartTrial && (
          <button onClick={onStartTrial} style={{
            width: '100%', marginBottom: 20,
            background: 'rgba(129,140,248,0.08)',
            border: '1.5px solid rgba(129,140,248,0.3)',
            borderRadius: 16, padding: '14px 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
          }}>
            <span style={{ fontSize: 20 }}>🎯</span>
            <div style={{ textAlign: 'right', flex: 1, marginRight: 12 }}>
              <div style={{ color: '#7c6df8', fontWeight: 800, fontSize: 15 }}>{t('home_trialExam')}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>{t('home_trialExamDesc')}</div>
            </div>
            <span style={{ color: '#7c6df8', fontSize: 18 }}>←</span>
          </button>
        )}

        {/* ── آخر الجلسات ── */}
        <div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            {t('home_recentSessions')}
          </div>

          {sessions.length === 0 ? (
            <div style={{
              background: 'var(--bg-card)', borderRadius: 16,
              border: '1px solid #334155', padding: '28px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📝</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>{t('home_noSessions')}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>{t('home_noSessionsDesc')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sessions.map((s, i) => {
                const score = s.score ? Math.round(s.score) : 0;
                const scoreColor = score >= 70 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';
                const date = s.ended_at
                  ? new Date(s.ended_at).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' })
                  : '';
                return (
                  <div key={i} style={{
                    background: 'var(--bg-card)', borderRadius: 14,
                    border: '1px solid #334155',
                    padding: '14px 16px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700 }}>
                        {s.tests?.name_ar || t('home_trainingSession')}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                        {s.total_questions} {t('home_questions')} • {date}
                      </div>
                    </div>
                    <div style={{
                      background: scoreColor + '22',
                      border: `1px solid ${scoreColor}44`,
                      borderRadius: 10, padding: '6px 12px',
                      color: scoreColor, fontWeight: 900, fontSize: 16,
                    }}>
                      {score}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── زر شاركنا رأيك (للمشتركين فقط) ── */}
        {!user?.isGuest && plan && (
          <button onClick={() => { setShowReview(true); setReviewSent(false); setReviewStars(0); setReviewName(''); setReviewComment(''); }} style={{
            width: '100%', marginBottom: 12,
            background: 'rgba(251,191,36,0.07)',
            border: '1.5px solid rgba(251,191,36,0.25)',
            borderRadius: 16, padding: '14px 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
          }}>
            <span style={{ fontSize: 20 }}>⭐</span>
            <div style={{ textAlign: 'right', flex: 1, marginRight: 12 }}>
              <div style={{ color: '#fbbf24', fontWeight: 800, fontSize: 15 }}>شاركنا تجربتك</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>اكتب رأيك في قادر</div>
            </div>
            <span style={{ color: '#fbbf24', fontSize: 18 }}>←</span>
          </button>
        )}

        {/* ── modal الرأي ── */}
        {showReview && (
          <div onClick={() => setShowReview(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: '#1a1f35', border: '1px solid #2d3554',
              borderRadius: 20, padding: '28px 22px', width: '100%', maxWidth: 400,
              fontFamily: 'Tajawal,sans-serif', direction: 'rtl',
            }}>
              {reviewSent ? (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <div style={{ fontSize: 56, marginBottom: 12 }}>🙏</div>
                  <div style={{ color: '#fff', fontSize: 20, fontWeight: 900, marginBottom: 8 }}>شكراً على رأيك!</div>
                  <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 24 }}>سيظهر رأيك بعد مراجعته</div>
                  <button onClick={() => setShowReview(false)} style={{
                    background: 'linear-gradient(135deg,#7c6df8,#a78bfa)',
                    border: 'none', borderRadius: 12, padding: '12px 32px',
                    color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                  }}>حسناً</button>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 6 }}>💬</div>
                  <div style={{ color: '#fff', fontSize: 18, fontWeight: 900, textAlign: 'center', marginBottom: 4 }}>شاركنا تجربتك</div>
                  <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginBottom: 20 }}>رأيك يساعدنا نتطور</div>

                  {/* النجوم */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 6 }}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n}
                        onClick={() => setReviewStars(n)}
                        onMouseEnter={() => setReviewHover(n)}
                        onMouseLeave={() => setReviewHover(0)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', fontSize: 38, padding: 0,
                          filter: (reviewHover || reviewStars) >= n ? 'none' : 'grayscale(1) opacity(0.3)',
                          transform: (reviewHover || reviewStars) >= n ? 'scale(1.15)' : 'scale(1)',
                          transition: 'all .15s',
                        }}>⭐</button>
                    ))}
                  </div>
                  {(reviewHover || reviewStars) > 0 && (
                    <div style={{
                      textAlign: 'center', fontWeight: 700, fontSize: 14, marginBottom: 16,
                      color: ['','#ef4444','#f97316','#f59e0b','#22c55e','#7c6df8'][(reviewHover||reviewStars)],
                    }}>
                      {['','ضعيف 😕','مقبول 😐','جيد 🙂','جيد جداً 😊','ممتاز 🔥'][(reviewHover||reviewStars)]}
                    </div>
                  )}

                  {/* الاسم */}
                  <input value={reviewName} onChange={e => setReviewName(e.target.value)}
                    placeholder="اسمك (اختياري)"
                    style={{
                      width: '100%', boxSizing: 'border-box', marginBottom: 10,
                      background: '#0f1423', border: '1px solid #2d3554',
                      borderRadius: 12, padding: '11px 14px',
                      color: '#fff', fontSize: 14, fontFamily: 'Tajawal,sans-serif',
                      outline: 'none', direction: 'rtl',
                    }}
                  />

                  {/* التعليق */}
                  <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)}
                    placeholder="اكتب تجربتك مع قادر..."
                    rows={4}
                    style={{
                      width: '100%', boxSizing: 'border-box', marginBottom: 16,
                      background: '#0f1423', border: '1px solid #2d3554',
                      borderRadius: 12, padding: '11px 14px',
                      color: '#fff', fontSize: 14, fontFamily: 'Tajawal,sans-serif',
                      resize: 'none', outline: 'none', direction: 'rtl',
                    }}
                  />

                  <button
                    disabled={reviewStars === 0 || reviewLoading}
                    onClick={async () => {
                      if (reviewStars === 0) return;
                      setReviewLoading(true);
                      await saveReview({ displayName: reviewName.trim() || 'مستخدم', stars: reviewStars, comment: reviewComment.trim() });
                      setReviewLoading(false);
                      setReviewSent(true);
                    }}
                    style={{
                      width: '100%', padding: '13px 0',
                      background: reviewStars > 0 ? 'linear-gradient(135deg,#7c6df8,#a78bfa)' : '#1e2540',
                      border: 'none', borderRadius: 12,
                      color: reviewStars > 0 ? '#fff' : '#4a5568',
                      fontSize: 15, fontWeight: 800,
                      cursor: reviewStars > 0 ? 'pointer' : 'default',
                      fontFamily: 'Tajawal,sans-serif', marginBottom: 8, transition: 'all .2s',
                    }}
                  >{reviewLoading ? '...' : reviewStars === 0 ? 'اختر تقييمك أولاً' : 'إرسال الرأي'}</button>

                  <button onClick={() => setShowReview(false)} style={{
                    width: '100%', background: 'none', border: 'none',
                    color: '#64748b', fontSize: 13, cursor: 'pointer', fontFamily: 'Tajawal,sans-serif', padding: 8,
                  }}>إلغاء</button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── عن قادر ── */}
        <div style={{
          marginTop: 32,
          background: 'linear-gradient(135deg,#1a1060,#2d1b8e)',
          borderRadius: 18, padding: '28px 20px',
          border: '1px solid rgba(255,215,0,0.15)',
        }}>
          {/* العنوان */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <LogoIcon size={32} />
            <div>
              <div style={{ color: '#FFD700', fontSize: 20, fontWeight: 900, fontFamily: 'Tajawal,sans-serif' }}>{t('appName')}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{t('home_aboutTitle')}</div>
            </div>
          </div>

          {/* الرسالة */}
          <p style={{
            color: 'rgba(255,255,255,0.8)', fontSize: 13, lineHeight: 1.9,
            fontFamily: 'Tajawal,sans-serif', marginBottom: 16,
            borderRight: isRTL ? '3px solid #FFD700' : 'none',
            borderLeft: isRTL ? 'none' : '3px solid #FFD700',
            paddingRight: isRTL ? 12 : 0,
            paddingLeft: isRTL ? 0 : 12,
          }}>
            {t('home_aboutDesc')}
          </p>

          {/* المميزات */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
            {[
              { icon: '🎓', key: 'home_feature1' },
              { icon: '🏆', key: 'home_feature2' },
              { icon: '📊', key: 'home_feature3' },
              { icon: '📋', key: 'home_feature4' },
            ].map(f => (
              <div key={f.key} style={{
                background: 'rgba(255,255,255,0.06)', borderRadius: 10,
                padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ fontSize: 18 }}>{f.icon}</span>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, fontFamily: 'Tajawal,sans-serif' }}>{t(f.key)}</span>
              </div>
            ))}
          </div>

          {/* روابط التواصل */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <a href="mailto:support@qadder.sa" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, textDecoration: 'none', fontFamily: 'Tajawal,sans-serif' }}>
                📧 support@qadder.sa
              </a>
              <a href="https://t.me/qadder_sa" target="_blank" rel="noreferrer" style={{ color: '#229ED9', fontSize: 12, textDecoration: 'none', fontFamily: 'Tajawal,sans-serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="16" height="16"><circle cx="24" cy="24" r="24" fill="#229ED9"/><path fill="#fff" d="M35.2 14.3 30.9 34c-.3 1.4-1.2 1.8-2.4 1.1l-6.6-4.9-3.2 3.1c-.4.4-.6.6-1.2.6l.4-6.7 12.2-11c.5-.5-.1-.7-.8-.3L14.4 26.1l-6.6-2.1c-1.4-.4-1.5-1.4.3-2.1l25.9-10c1.2-.4 2.2.3 1.2 2.4z"/></svg>
                قناة تيليقرام
              </a>
              <a href="https://x.com/qaddersa" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', fontSize: 12, textDecoration: 'none', fontFamily: 'Tajawal,sans-serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                @qaddersa
              </a>
            </div>
            <a href="/about.html" target="_blank" rel="noreferrer" style={{
              color: '#FFD700', fontSize: 12, fontWeight: 700,
              textDecoration: 'none', fontFamily: 'Tajawal,sans-serif',
            }}>
              {t('readMore')}
            </a>
          </div>
        </div>

      </div>
    </>
  );
}
